import { useRef, useState } from 'react';
import { X, Plus, Minus, ShoppingBag, Trash2, ShieldCheck } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useProducts } from '../context/ProductsContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { toast } from 'sonner';

const THRESHOLD = 25;
const fmt = (n) => n.toFixed(2).replace('.', ',');

function CartLine({ line, updateQty, removeItem }) {
  const [offset, setOffset] = useState(0);
  const [removing, setRemoving] = useState(false);
  const startX = useRef(null);
  const offsetRef = useRef(0);

  const setOff = (v) => { offsetRef.current = v; setOffset(v); };
  const onTouchStart = (e) => { startX.current = e.touches[0].clientX; };
  const onTouchMove = (e) => {
    if (startX.current == null) return;
    const delta = e.touches[0].clientX - startX.current;
    if (delta < 0) setOff(Math.max(delta, -96));
  };
  const onTouchEnd = () => {
    if (offsetRef.current <= -60) {
      setRemoving(true);
      setTimeout(() => removeItem(line.product_id), 180);
    } else {
      setOff(0);
    }
    startX.current = null;
  };

  return (
    <div className="relative overflow-hidden rounded-lg" data-testid={`cart-line-${line.product_id}`}>
      {/* Swipe-to-delete background (mobile) */}
      <div className="absolute inset-y-0 right-0 w-24 bg-red-500 flex items-center justify-center rounded-lg">
        <Trash2 size={20} strokeWidth={1.5} className="text-white" />
      </div>
      <div
        className={`relative flex gap-3 bg-essenya-bg ${removing ? 'transition-all duration-200' : offset === 0 ? 'transition-transform duration-200' : ''}`}
        style={{ transform: `translateX(${removing ? '-100%' : offset + 'px'})`, opacity: removing ? 0 : 1 }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img src={line.product.images[0]} alt={line.product.name} className="w-20 h-24 object-contain p-1 rounded-lg bg-essenya-surface2" />
        <div className="flex-1 flex flex-col">
          <div className="flex justify-between gap-2">
            <div>
              <p className="font-medium text-sm">{line.product.name}</p>
              <p className="text-xs text-essenya-text">{line.product.fragranceLabel}</p>
            </div>
            <button onClick={() => removeItem(line.product_id)} data-testid={`cart-remove-${line.product_id}`} aria-label="Rimuovi"><Trash2 size={16} strokeWidth={1.5} className="text-essenya-text hover:text-essenya-ink" /></button>
          </div>
          <div className="mt-auto flex items-center justify-between">
            <div className="flex items-center border border-essenya-border rounded-lg bg-white">
              <button onClick={() => updateQty(line.product_id, line.quantity - 1)} className="p-2" aria-label="Diminuisci"><Minus size={14} strokeWidth={1.5} /></button>
              <span className="px-2 text-sm w-8 text-center">{line.quantity}</span>
              <button onClick={() => updateQty(line.product_id, line.quantity + 1)} className="p-2" aria-label="Aumenta"><Plus size={14} strokeWidth={1.5} /></button>
            </div>
            <span className="font-display text-sm">{fmt(line.product.price * line.quantity)}€</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CartDrawer() {
  const { isOpen, setIsOpen, lines, subtotal, count, updateQty, removeItem, addItem } = useCart();
  const { guardPurchase } = useSiteSettings();
  const { products } = useProducts();
  const navigate = useNavigate();
  const remaining = Math.max(0, THRESHOLD - subtotal);
  const progress = Math.min(100, (subtotal / THRESHOLD) * 100);

  // Upsell: suggest a product not in cart
  const inCart = new Set(lines.map(l => l.product_id));
  const upsell = products.filter(p => !inCart.has(p.id) && p.category !== 'bundle' && !p.hidden).slice(0, 1)[0];

  const goCheckout = () => {
    if (guardPurchase()) return;
    setIsOpen(false);
    navigate('/checkout');
  };

  const addUpsell = () => {
    addItem(upsell.id, 1);
    toast.success(`${upsell.name} aggiunto al carrello`);
  };

  return (
    <div className={`fixed inset-0 z-50 ${isOpen ? '' : 'pointer-events-none'}`} data-testid="cart-drawer">
      <div className={`absolute inset-0 bg-black/40 transition-opacity ${isOpen ? 'opacity-100' : 'opacity-0'}`} onClick={() => setIsOpen(false)} />
      <aside className={`absolute top-0 right-0 h-full w-full sm:w-[440px] liquid-glass-panel border-y-0 border-r-0 rounded-l-[2rem] shadow-2xl flex flex-col transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex items-center justify-between p-5 border-b border-essenya-border">
          <div className="font-display text-lg">Carrello ({count})</div>
          <button data-testid="cart-close" onClick={() => setIsOpen(false)} aria-label="Chiudi"><X size={22} strokeWidth={1.5} /></button>
        </div>

        {subtotal > 0 && (
          <div className="p-5 border-b border-essenya-border">
            {remaining > 0 ? (
              <p className="text-sm mb-2">Ti mancano <strong>{fmt(remaining)}€</strong> per la spedizione gratuita</p>
            ) : (
              <p className="text-sm mb-2 text-essenya-ink">Hai sbloccato la spedizione gratuita</p>
            )}
            <div className="h-1.5 bg-essenya-surface2 rounded-full overflow-hidden">
              <div className="h-full bg-essenya-ink transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {lines.length === 0 && (
            <div className="text-center py-16 text-essenya-text">
              <ShoppingBag size={32} strokeWidth={1.2} className="mx-auto mb-3 opacity-60" />
              <p>Il tuo carrello è vuoto</p>
              <Link to="/shop" onClick={() => setIsOpen(false)} className="btn-primary mt-6 text-sm">Inizia lo shopping</Link>
            </div>
          )}
          {lines.length > 0 && (
            <p className="sm:hidden text-[11px] text-essenya-text text-center -mt-1">Scorri un prodotto verso sinistra per rimuoverlo</p>
          )}
          {lines.map(line => (
            <CartLine key={line.product_id} line={line} updateQty={updateQty} removeItem={removeItem} />
          ))}

          {lines.length > 0 && upsell && (
            <div className="mt-6 p-4 rounded-xl bg-essenya-surface2 border border-essenya-border">
              <p className="text-xs text-essenya-text mb-2">Aggiungi un altro prodotto e crea il tuo Bundle Casa</p>
              <div className="flex gap-3 items-center">
                <img src={upsell.images[0]} alt="" className="w-14 h-16 object-contain p-1 rounded-md bg-white" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{upsell.name}</p>
                  <p className="text-xs text-essenya-text">{fmt(upsell.price)}€</p>
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <button onClick={addUpsell} data-testid="cart-upsell-add" className="text-xs font-medium bg-essenya-ink text-white rounded-md px-3 py-2 hover:bg-essenya-ink/90 transition">Acquista</button>
                  <Link to={`/prodotto/${upsell.slug}`} onClick={() => setIsOpen(false)} data-testid="cart-upsell-link" className="text-xs font-medium text-center border border-essenya-ink rounded-md px-3 py-1.5 hover:bg-essenya-ink hover:text-white transition">Vedi</Link>
                </div>
              </div>
            </div>
          )}
        </div>

        {lines.length > 0 && (
          <div className="border-t border-essenya-border p-5 space-y-3">
            <div className="flex justify-between text-sm"><span>Subtotale</span><span className="font-medium" data-testid="cart-subtotal">{fmt(subtotal)}€</span></div>
            <button onClick={goCheckout} data-testid="cart-checkout-button" className="btn-primary w-full">Vai al checkout</button>
            <button onClick={() => setIsOpen(false)} data-testid="cart-continue-shopping" className="btn-secondary w-full">Continua lo shopping</button>
            <p className="flex items-center gap-1.5 text-xs text-essenya-text justify-center"><ShieldCheck size={14} strokeWidth={1.5} /> Pagamento sicuro e ordine tracciato</p>
          </div>
        )}
      </aside>
    </div>
  );
}
